import { useMemo, useState } from 'react';

import { type EntryData, EntryStatus } from 'chaika-frontend-types/frontend-cms';

import { DateHelper } from '@utils/DateHelper';
import { isDefined } from '@utils/isDefined';

import { entryService } from '@patient/services/entry/entryService';
import { store } from '@store';

interface Params {
    entry: EntryData;
}

interface UseDateForm {
    creationTime: string;
    visitTime?: string;
    canSave: boolean;
    canEdit: boolean;
    onTimeChange(date: Date): void;
    onSave(): Promise<void>;
}

export function useDateForm({ entry }: Params): UseDateForm {
    const { app } = store;

    const creationTime = DateHelper.toLocalStringDate(entry.creationTime);

    const [visitTime, setVisitTime] = useState<string>(
        DateHelper.toLocalStringDate(entry.visitTime ?? entry.creationTime),
    );

    const canSave = useMemo(() => {
        const haveVisitTime = isDefined(visitTime);

        const visitTimeChanged = entry.visitTime
            ? visitTime !== DateHelper.toLocalStringDate(entry.visitTime ?? entry.creationTime)
            : true;

        return haveVisitTime && visitTimeChanged;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visitTime, creationTime, entry.visitTime, entry.creationTime]);

    const canEdit = entry.status !== EntryStatus.Signed;

    function onTimeChange(date: Date): void {
        const visitTime = DateHelper.toLocalStringDate(date);
        setVisitTime(visitTime);
    }

    async function onSave(): Promise<void> {
        if (canSave) {
            const formattedVisitTime = formatVisitTime(entry.creationTime, visitTime);
            await entryService.editVisitTime(formattedVisitTime);
            app.resetRightMenu();
        }
    }

    return {
        creationTime,
        visitTime,
        canSave,
        canEdit,
        onTimeChange,
        onSave,
    };
}

function formatVisitTime(currentDate: string, newDate: string) {
    const time = currentDate.slice(10);
    const date = newDate.split('.').reverse().join('-');

    return `${date}${time}`;
}
